import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Link,
  ListItem,
  Text,
  UnorderedList,
  useToast,
} from "@chakra-ui/react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { useCallback, useEffect, useRef } from "react";
import { urls } from "../../urls";
import { CriticalError } from "./CriticalError/CriticalError";
import { useRumError } from "./RumComponentContext/useRumError";
import { isApiErrorDto } from "./isApiErrorDto";
import { isFetchBaseQueryError } from "./isFetchBaseQueryError";
import { UnsavedChangesError } from "../editing/save-coordinator/UnsavedChangesError";
import { onChangesSaved } from "../editing/save-coordinator/changesSaved";
import { failedRequestOf, type FailedRequest } from "../redux/withFailedRequest";

// Dynamic keys referenced via t(e.data.errorCode) below; magic comment keeps langen from pruning them.
// t('DOFFIN_VALIDATION_FAILED') t('DOFFIN_SCHEMATRON_FAILED') t('DOFFIN_SCHEMATRON_ERROR') t('VERSION_CONFLICT')

const UNSAVED_CHANGES_TOAST = "unsaved-changes";
const ERROR_TOAST_MS = 5000;

export interface NoAccessOptions {
  message: string;
  destination: { href: string; label: string };
}

export const useApiError = () => {
  const toast = useToast();
  const rumError = useRumError();
  const repeatsShown = useRef(new Map<string, number>());

  useEffect(
    () =>
      onChangesSaved(() => {
        if (toast.isActive(UNSAVED_CHANGES_TOAST)) toast.close(UNSAVED_CHANGES_TOAST);
      }),
    [toast],
  );

  const trigger = useCallback(
    (e: unknown, whatFailed?: string) => {
      if (e instanceof UnsavedChangesError && e.alreadyReported) return;
      if (e instanceof UnsavedChangesError) {
        rumError(e, { message: "Changes could not be saved", unsavedChanges: { reason: e.reason } });
        const options = {
          status: "error" as const,
          duration: null,
          isClosable: true,
          ...(e.reason === "fieldsNeedCorrecting"
            ? {
                title: t("Some fields need correcting before you can continue"),
                description: t("Correct the fields marked in red, then try again."),
              }
            : {
                title: t("Your changes could not be saved"),
                description: t("Nothing was carried out. Try again in a moment."),
              }),
        };
        if (toast.isActive(UNSAVED_CHANGES_TOAST)) toast.update(UNSAVED_CHANGES_TOAST, options);
        else toast({ id: UNSAVED_CHANGES_TOAST, ...options });
        return;
      }
      const failure = describeFailure(e);
      const message = failureMessage(failure);
      rumError(e instanceof Error ? e : new ApiCallError(message), { message, apiError: failure });
      const id = `api-error:${failure.status}:${failure.errorCode ?? ""}:${fieldErrorsKey(e)}`;
      const options = {
        title: whatFailed ?? t("Whooops!"),
        description: errorDescription(e),
        status: "error" as const,
        isClosable: true,
      };
      if (!toast.isActive(id)) {
        toast({ id, ...options, duration: ERROR_TOAST_MS });
        return;
      }
      const repeats = (repeatsShown.current.get(id) ?? 0) + 1;
      repeatsShown.current.set(id, repeats);
      // Chakra v2's toast restarts its close timer only when `duration` changes.
      toast.update(id, { ...options, duration: ERROR_TOAST_MS + (repeats % 2) });
    },
    [rumError, toast]
  );

  const message = useCallback(
    (e: unknown): string =>
      (e && isFetchBaseQueryError(e) && isApiErrorDto(e.data) && e.data.errorCode
        ? t(e.data.errorCode)
        : t("Something went wrong!")) ?? "",
    [],
  );

  const show = useCallback(
    (e: FetchBaseQueryError | SerializedError): React.ReactElement => {
      rumError(e, { message: "Critical error message was displayed" });
      if (isFetchBaseQueryError(e) && isApiErrorDto(e.data)) {
        return <CriticalError title={t("Whooops!") ?? undefined} message={t(e.data.errorCode) ?? undefined} />;
      } else {
        return <CriticalError title={t("Whooops!") ?? undefined} message={t("Something went wrong!") ?? undefined} />;
      }
    },
    [rumError]
  );

  const showNoAccess = useCallback((options?: NoAccessOptions) => {
    return (
      <Box padding="10px">
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            {t("No access!")}
          </AlertTitle>
          {options ? (
            <AlertDescription>
              <Text>{options.message}</Text>
              <Link color="smPrimary" href={options.destination.href}>
                {options.destination.label}
              </Link>
            </AlertDescription>
          ) : (
            <AlertDescription>
              {t("You don't have access to this page")}
              <Text>
                {t("You can")}&nbsp;
                <Link color="smPrimary" href={urls.home}>
                  {t("click here")}
                </Link>
              </Text>
              {t("to get back to the home page")}.
            </AlertDescription>
          )}
        </Alert>
      </Box>
    );
  }, []);

  return {
    trigger,
    message,
    show,
    showNoAccess,
  };
};

type FailureSummary = { status: number | string; errorCode: string | null } & Partial<FailedRequest>;

class ApiCallError extends Error {
  override name = "ApiCallError";
}

const failureMessage = ({ method, route, status, errorCode }: FailureSummary): string =>
  `API call failed: ${method && route ? `${method} ${route} → ` : ""}${status} ${errorCode ?? "no error code"}`;

const describeFailure = (e: unknown): FailureSummary => {
  const errorCode = e && isFetchBaseQueryError(e) && isApiErrorDto(e.data) && e.data.errorCode ? e.data.errorCode : null;
  const request = failedRequestOf(e);
  const status = typeof e === "object" && e !== null && "status" in e ? e.status : undefined;
  if (typeof status === "number" || typeof status === "string") return { status, errorCode, ...request };
  return { status: e instanceof Error ? e.name : "unknown", errorCode, ...request };
};

const errorDescription = (e: unknown): React.ReactNode => {
  if (!(e && isFetchBaseQueryError(e) && isApiErrorDto(e.data))) return t("Something went wrong!");
  const fieldErrors = e.data.fieldErrors;
  if (fieldErrors && fieldErrors.length > 0) {
    return (
      <UnorderedList>
        {fieldErrors.map((f, i) => (
          <ListItem key={`${f.field}-${i}`}>{t(f.code, f.params ?? {})}</ListItem>
        ))}
      </UnorderedList>
    );
  }
  return e.data.errorCode ? t(e.data.errorCode) : t("Something went wrong!");
};

const fieldErrorsKey = (e: unknown): string =>
  e && isFetchBaseQueryError(e) && isApiErrorDto(e.data)
    ? JSON.stringify((e.data.fieldErrors ?? []).map((f) => [f.field, f.code, f.params ?? null]))
    : "";
